import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getEvents } from "core-integration/src/store/events/reducers";
import { getRules } from "core-integration/src/store/game/reducers";
import { getCreateClassicLeagueError } from "core-integration/src/store/leagues/reducers";
import { createClassicLeague } from "core-integration/src/store/leagues/thunks";
import { ICreateClassicLeagueData } from "core-integration/src/store/leagues/types";
import React, { useState } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { createInteractionData } from "../../utils/tealium";
import Button from "../Button";
import Copy from "../Copy";
import { FieldWrap, InputField, SelectField } from "../FieldRenderers";
import { Main, Wrapper } from "../Layout";
import Title from "../Title";

type Props = WithTranslation;

interface IState {
  name: string;
  startEvent: number;
}

const CreateClassic: React.FC<Props> = ({ t }) => {
  const dispatch = useDispatch<ThunkDispatch>();
  const navigate = useNavigate();
  const [state, setState] = useState<IState>({
    name: "",
    startEvent: 1,
  });

  const requiredFields: Array<keyof IState> = ["name", "startEvent"];

  const apiDataFromState = (): ICreateClassicLeagueData => ({
    name: state.name,
    start_event: state.startEvent,
  });

  const handleNameChange = (e: React.SyntheticEvent<HTMLInputElement>) =>
    setState({ ...state, name: e.currentTarget.value });

  const handleStartEventChange = (e: React.SyntheticEvent<HTMLSelectElement>) =>
    setState({ ...state, startEvent: parseInt(e.currentTarget.value, 10) });

  const error = useSelector((state: RootState) =>
    getCreateClassicLeagueError(state),
  );

  const events = useSelector((state: RootState) => getEvents(state));
  const rules = useSelector((state: RootState) => getRules(state));

  const handleSubmit = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    await dispatch(createClassicLeague(apiDataFromState())).then(() => {
      if (!error) {
        createInteractionData({
          contentTitle: "leagues",
          interactionId: "nba-fantasy-game:create-league:button",
          interactionText: t("createClassic.button", "Create league"),
        });
      }
    });
    navigate("/leagues");
  };

  const disabled = requiredFields.some((e) => !state[e]);
  return (
    <Wrapper>
      <Main>
        <Copy>
          <Title>
            {t("createClassic.title", "Create a new classic league")}
          </Title>
          {error ? (
            error.badRequest &&
            error.badRequest.non_field_errors &&
            error.badRequest.non_field_errors[0].code ===
              "max_private_entries" ? (
              <p>
                {t(
                  "createClassic.error",
                  "You may only enter {{ maxPrivateEntries }} private leagues",
                  {
                    maxPrivateEntries: rules && rules.league_join_private_max,
                  },
                )}
              </p>
            ) : (
              <p>{JSON.stringify(error)}</p>
            )
          ) : (
            ""
          )}
          <form onSubmit={handleSubmit}>
            <FieldWrap>
              <InputField
                id="ismLeagueName"
                label={t("createClassic.leagueName.label", "League name")}
                value={state.name}
                onChange={handleNameChange}
                hint={t(
                  "createClassic.leagueName.hint",
                  "Maximum 30 characters",
                )}
                maxLength={30}
              />
            </FieldWrap>
            <FieldWrap>
              <SelectField
                id="ismLeagueStartEvent"
                value={state.startEvent}
                onChange={handleStartEventChange}
                label={t("createClassic.startEventLabel", "Scoring starts")}
              >
                {events.map((e) => (
                  <option
                    value={e.id}
                    aria-selected={e.id === state.startEvent}
                    key={e.id}
                  >
                    {e.name}
                  </option>
                ))}
              </SelectField>
            </FieldWrap>
            <FieldWrap>
              <Button type="submit" disabled={disabled}>
                {t("createClassic.button", "Create league")}
              </Button>
            </FieldWrap>
          </form>
        </Copy>
      </Main>
    </Wrapper>
  );
};

export default withTranslation()(CreateClassic);
